import { Button } from '@klear/ui/components/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@klear/ui/components/tooltip'
import { dateFormattedOrDash, domainListItemEffectiveDate, domainListItemNumber } from '@klear/order-event/list-display'
import type { OrderEventTimelineItem } from '@klear/order-event/order-event-timeline-tree'
import {
  orderEventTypeLabel,
  isInvoiceOfferEvent,
  showsStatusToTrader
} from '@klear/order-event-ui/event-timeline-utils'
import { PERMISSION_ACTION_VIEW, PERMISSION_ENTITY_INVOICE_OFFER } from '@klear/constants'
import { orderEventShowsStatus } from '@klear/order-event/presentation'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useUnpaidLateFeesByOffer } from '@klear/ui/hooks/use-collections'
import { AlertTriangle } from 'lucide-react'

export function EventTitle({
  event,
  isAdminContext = true
}: {
  event: OrderEventTimelineItem
  isAdminContext?: boolean
}) {
  const { user } = useAuth()
  const isOffer = isInvoiceOfferEvent(event)
  const traderId = isOffer ? event.model.traderId : undefined
  const offerId = isOffer ? event.model.id : undefined
  const canViewUnpaidLateFees = user.hasPermission(PERMISSION_ENTITY_INVOICE_OFFER, PERMISSION_ACTION_VIEW)
  const { rows: unpaidLateFees } = useUnpaidLateFeesByOffer(traderId, undefined, canViewUnpaidLateFees && isOffer)
  const hasUnpaidLateFee =
    offerId != null && unpaidLateFees.some((row) => row.offerId === offerId && row.remaining.isPositive())
  const number = domainListItemNumber(event)
  const accruingLabel = isAdminContext ? 'Accruing late fees' : 'Accruing extension fees'
  return (
    <>
      <div className="inline-flex items-center gap-1.5">
        <span className="text-xs text-muted-foreground">{event.envelope.idDisplay}</span>
        {hasUnpaidLateFee ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex" tabIndex={0} aria-label={accruingLabel}>
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />
                </span>
              </TooltipTrigger>
              <TooltipContent>{accruingLabel}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </div>
      <div className="font-medium">
        {orderEventTypeLabel(event.type)}
        {number ? ` #${number}` : ''}
      </div>
      <div className="text-sm text-muted-foreground">{dateFormattedOrDash(domainListItemEffectiveDate(event))}</div>
      {orderEventShowsStatus(event.type) && (isAdminContext || showsStatusToTrader(event)) ? (
        <Button variant="outline" size="xs">
          {event.envelope.statusLabel}
        </Button>
      ) : null}
    </>
  )
}
